<template>
  <v-card class="mb-7">
    <v-card-text class="pa-5">

      <div class="d-sm-flex align-center justify-space-between mb-3">
        <div class="ml-3">
          <h2 class="font-weight-regular title">Códigos de Asesor</h2>
          <h6 class="body-2 mb-1 subtitle-2 font-weight-light">Gestión de códigos de asesores</h6>
        </div>
        <v-btn color="primary" @click="openDialog(null)">
          <v-icon left>mdi-plus</v-icon>
          Crear código de asesor
        </v-btn>
      </div>

      <div class="d-flex align-center flex-wrap pa-3">
        <v-select
          v-model="filters.seccion"
          label="Sección"
          class="mr-5"
          :items="secciones"
          item-text="nombre"
          item-value="id"
          outlined
          dense
          style="max-width: 210px;"
          hide-details
          clearable
          @change="filters.page = 1"
        />
        <v-select
          v-model="filters.activo"
          label="Activo"
          class="mr-5"
          :items="activoItems"
          item-text="text"
          item-value="value"
          outlined
          dense
          style="max-width: 180px;"
          hide-details
          clearable
          @change="filters.page = 1"
        />
        <v-select
          v-model="filters.usado"
          label="Uso"
          class="mr-5"
          :items="usadoItems"
          item-text="text"
          item-value="value"
          outlined
          dense
          style="max-width: 160px;"
          hide-details
          clearable
          @change="filters.page = 1"
        />
        <v-switch
          v-model="filters.soloMios"
          label="Solo los míos"
          class="mr-5 mt-1"
          hide-details
          @change="filters.page = 1"
        />
        <BaseDataRange
          style="max-width: 474px; margin: 0;"
          @sendStartDate="sendStartDate"
          @sendEndDate="sendEndDate"
          @clearStartDate="filters.startDate = ''"
          @clearEndDate="filters.endDate = ''"
        />
      </div>

      <div class="pa-3 pt-0" style="max-width: 420px;">
        <v-text-field
          v-model="searchLocal"
          prepend-inner-icon="mdi-magnify"
          dense
          outlined
          clearable
          hide-details
          label="Buscar por código, sección o asesor"
          @input="onLocalSearchInput"
        />
      </div>

      <v-data-table
        :headers="headerArray"
        :items="codigos"
        :page.sync="filters.page"
        :items-per-page="pageSize"
        :server-items-length="totalCount"
        :loading="loading"
        hide-default-footer
      >
        <template v-slot:item.seccion="{ item }">
          <span>{{ item.seccion ? (item.seccion.nombre || item.seccion.slug || item.seccion.id) : '-' }}</span>
        </template>

        <template v-slot:item.precio_primera_cuota="{ item }">
          <span class="text-no-wrap">S/. {{ item.precio_primera_cuota }}</span>
        </template>

        <template v-slot:item.monto_descuento="{ item }">
          <span class="text-no-wrap">{{ item.monto_descuento != null ? `S/. ${item.monto_descuento}` : '-' }}</span>
        </template>

        <template v-slot:item.codigo_referencia="{ item }">
          <span>{{ item.codigo_referencia ? item.codigo_referencia.codigo : '-' }}</span>
        </template>

        <template v-slot:item.creado_por="{ item }">
          <span>{{ item.creado_por ? (((item.creado_por.first_name || '') + ' ' + (item.creado_por.last_name || '')).trim() || item.creado_por.username || '-') : '-' }}</span>
        </template>

        <template v-slot:item.activo="{ item }">
          <div class="d-flex justify-center">
            <v-chip small label :color="item.activo ? 'success' : 'grey darken-1'" dark class="font-weight-bold">
              {{ item.activo ? 'Activo' : 'Inactivo' }}
            </v-chip>
          </div>
        </template>

        <template v-slot:item.usado="{ item }">
          <div class="d-flex justify-center">
            <v-chip small label :color="item.usado ? 'error' : 'blue-grey lighten-3'" dark class="font-weight-bold">
              {{ item.usado ? 'Usado' : 'Disponible' }}
            </v-chip>
          </div>
        </template>

        <template v-slot:item.created="{ item }">
          <span class="text-no-wrap">{{ item.created | moment("DD/MM/YYYY HH:mm") }}</span>
        </template>

        <template v-slot:item.acciones="{ item }">
          <div class="d-flex">
            <v-btn
              small
              class="actions-btn mr-2"
              @click="openDialog(item)"
            >
              <v-icon small left>mdi-pencil</v-icon>
              Modificar
            </v-btn>
            <v-btn
              small
              :class="item.activo ? 'actions-btn actions-btn--danger' : 'actions-btn actions-btn--activate'"
              :loading="item._deactivating"
              @click="confirmDeactivate(item)"
            >
              <v-icon small left>{{ item.activo ? 'mdi-cancel' : 'mdi-check-circle' }}</v-icon>
              {{ item.activo ? 'Desactivar' : 'Activar' }}
            </v-btn>
          </div>
        </template>

        <template v-slot:footer="slotProps">
          <div class="pagination d-flex ml-auto mr-0 mt-5">
            <v-btn icon @click="filters.page > 1 ? filters.page-- : ''" :disabled="filters.page === 1">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <p class="ma-auto">{{ slotProps.props.pagination.page }} de {{ totalPages }}</p>
            <v-btn icon @click="filters.page < totalPages ? filters.page++ : ''" :disabled="next === null">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card-text>

    <v-dialog v-model="dialog.show" width="640" persistent>
      <v-card class="rounded-lg">
        <v-form ref="form" @submit.prevent="saveCode">
          <v-card-title class="py-6 px-8">
            <span class="dialog__title">
              {{ dialog.editItem ? 'Modificar código de asesor' : 'Crear código de asesor' }}
            </span>
          </v-card-title>

          <v-card-text class="px-8">
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="form.seccion"
                  label="Sección (Curso)"
                  outlined
                  dense
                  :items="secciones"
                  item-text="nombre"
                  item-value="id"
                  :rules="[rules.required]"
                  :error-messages="formErrors.seccion"
                  :loading="seccionLoading"
                  @change="onSeccionChange"
                  @focus="formErrors.seccion = ''"
                />
                <div v-if="pagoMinimo !== null" class="caption info--text mt-n3 mb-3">
                  <v-icon x-small color="info">mdi-information-outline</v-icon>
                  El precio mínimo permitido para este curso es <strong>S/. {{ pagoMinimo }}</strong>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.precio_primera_cuota"
                  label="Precio primera cuota (S/.)"
                  outlined
                  dense
                  type="number"
                  min="0"
                  :rules="[rules.required, minPrecioRule]"
                  :error-messages="formErrors.precio_primera_cuota"
                  @focus="formErrors.precio_primera_cuota = ''"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.monto_descuento"
                  label="Descuento fijo (S/.)"
                  outlined
                  dense
                  type="number"
                  min="0"
                  clearable
                  :disabled="!!form.codigoReferenciaInput || codigoRefValidado"
                  :error-messages="formErrors.monto_descuento"
                  hint="Excluyente con código de referencia (%). Vacío = sin descuento fijo."
                  persistent-hint
                  @input="onMontoDescuentoInput"
                  @focus="formErrors.monto_descuento = ''"
                />
              </v-col>

              <v-col cols="12" md="6">
                <div class="d-flex align-start">
                  <v-text-field
                    v-model="form.codigoReferenciaInput"
                    label="Código de referencia (opcional)"
                    outlined
                    dense
                    clearable
                    :disabled="hasMontoDescuento"
                    :error-messages="codigoRefError || formErrors.codigo_referencia"
                    :success-messages="codigoRefSuccess"
                    hint="Excluyente con descuento fijo. Solo mayúsculas y números."
                    persistent-hint
                    @input="onCodigoRefInput"
                    @keydown.space.prevent
                    style="flex: 1;"
                  />
                  <v-btn
                    class="ml-2 mt-1"
                    color="info"
                    outlined
                    small
                    :loading="codigoRefLoading"
                    :disabled="!form.codigoReferenciaInput || hasMontoDescuento"
                    @click="validarCodigoReferencia"
                  >
                    Validar
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions class="px-8 pb-6 d-flex justify-end">
            <v-btn class="actions-btn mr-2" @click="closeDialog">Cancelar</v-btn>
            <v-btn
              type="submit"
              :loading="dialog.loading"
              class="actions-btn actions-btn--save"
            >
              {{ dialog.editItem ? 'Guardar cambios' : 'Crear' }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- Modal de confirmación desactivar -->
    <ConfirmModal ref="confirmModal" />
  </v-card>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import BaseDataRange from '@/components/commonComponents/BaseDataRange'
import ConfirmModal from '@/components/emergentesGlobales/ConfirmModal'
import { required, maxLength } from '@/utils/rules.js'

const API_BASE = 'api/asesores/codigos/'

export default {
  name: 'TheCodeAdvisorTable',
  components: {
    BaseDataRange,
    ConfirmModal,
  },
  data: () => ({
    loading: false,
    codigos: [],
    pageSize: 15,
    totalCount: 0,
    next: null,
    searchLocal: '',
    secciones: [],
    timeout: null,
    rules: { required, maxLength },
    activoItems: [
      { text: 'Todos',     value: '' },
      { text: 'Activos',   value: 'true' },
      { text: 'Inactivos', value: 'false' },
    ],
    usadoItems: [
      { text: 'Disponible', value: 'false' },
      { text: 'Usado',      value: 'true' },
    ],
    headerArray: [
      { text: 'Código',           value: 'codigo',               sortable: false, align: 'start' },
      { text: 'Sección',          value: 'seccion',              sortable: false, align: 'start' },
      { text: 'Precio 1ª cuota', value: 'precio_primera_cuota', sortable: false, align: 'center' },
      { text: 'Desc. fijo',      value: 'monto_descuento',      sortable: false, align: 'center' },
      { text: 'Cód. referencia', value: 'codigo_referencia',    sortable: false, align: 'center' },
      { text: 'Creado por',      value: 'creado_por',           sortable: false, align: 'center' },
      { text: 'Activo',          value: 'activo',               sortable: false, align: 'center' },
      { text: 'Uso',             value: 'usado',                sortable: false, align: 'center' },
      { text: 'Creado',          value: 'created',              sortable: false, align: 'center' },
      { text: 'Acciones',        value: 'acciones',             sortable: false, align: 'center', width: '240px' },
    ],
    filters: {
      startDate: '',
      endDate: '',
      page: 1,
      search: '',
      seccion: null,
      activo: '',
      usado: null,
      soloMios: true,
    },
    dialog: {
      show: false,
      loading: false,
      editItem: null,
    },
    form: {
      seccion: null,
      precio_primera_cuota: '',
      monto_descuento: '',
      codigo_referencia: null,
      codigoReferenciaInput: '',
    },
    formErrors: {
      seccion: '',
      precio_primera_cuota: '',
      monto_descuento: '',
      codigo_referencia: '',
    },
    pagoMinimo: null,
    seccionLoading: false,
    codigoRefLoading: false,
    codigoRefError: '',
    codigoRefSuccess: '',
    codigoRefValidado: false,
  }),
  computed: {
    ...mapState({
      searchGlobal: (s) => s.searchGlobal,
      seccionesStore: (s) => s.secciones || [],
    }),
    totalPages () {
      return Math.ceil(this.totalCount / this.pageSize) || 1
    },
    minPrecioRule () {
      const min = this.pagoMinimo
      return (v) => {
        if (!v && v !== 0) return 'Este campo es necesario.'
        if (min !== null && Number(v) < Number(min)) {
          return `El precio no puede ser menor al mínimo del curso (S/. ${min}).`
        }
        return true
      }
    },
    hasMontoDescuento () {
      const v = this.form.monto_descuento
      return v !== null && v !== undefined && String(v).trim() !== ''
    },
  },
  watch: {
    async searchGlobal () {
      this.filters.page = 1
      await this.fetchData()
    },
    filters: {
      deep: true,
      async handler () {
        await this.fetchData()
      },
    },
    seccionesStore (val) {
      if (Array.isArray(val) && val.length) this.secciones = val
    },
  },
  methods: {
    ...mapMutations(['SET_SEARCH']),

    async fetchCodigos () {
      const q = this.$route.query
      let url = `${API_BASE}?page_size=${this.pageSize}`
      url += `&page=${q.page || 1}`
      url += `&search=${q.search || ''}`
      url += `&seccion=${q.seccion || ''}`
      url += `&activo=${q.activo ?? ''}`
      url += `&usado=${q.usado || ''}`
      url += `&solo_mios=${q.solo_mios || ''}`
      url += `&fecha_inicio=${q.fecha_inicio || ''}`
      url += `&fecha_fin=${q.fecha_fin || ''}`
      const { data } = await this.$http.get(url)
      const isArray = Array.isArray(data)
      this.next = isArray ? null : data.next
      this.codigos = isArray ? data : (data.results || [])
      this.totalCount = isArray ? data.length : (data.count || 0)
    },

    async fetchSecciones () {
      const { data } = await this.$http.get('api/matriculas/secciones/')
      this.secciones = data
    },

    async onSeccionChange (id) {
      this.pagoMinimo = null
      this.formErrors.precio_primera_cuota = ''
      if (!id) return
      this.seccionLoading = true
      try {
        const { data } = await this.$http.get(`api/matriculas/secciones/${id}/`)
        this.pagoMinimo = data.pago_minimo
      } catch {
        this.pagoMinimo = null
      } finally {
        this.seccionLoading = false
      }
    },

    onMontoDescuentoInput () {
      this.formErrors.monto_descuento = ''
      if (this.hasMontoDescuento) {
        this.form.codigoReferenciaInput = ''
        this.form.codigo_referencia = null
        this.codigoRefError = ''
        this.codigoRefSuccess = ''
        this.codigoRefValidado = false
      }
    },

    onCodigoRefInput (val) {
      const sanitized = (val || '').toUpperCase().replace(/[^A-Z0-9]/g, '')
      if (sanitized !== (val || '').toUpperCase()) {
        this.$nextTick(() => { this.form.codigoReferenciaInput = sanitized })
      } else {
        this.form.codigoReferenciaInput = sanitized
      }
      this.codigoRefError = ''
      this.codigoRefSuccess = ''
      this.codigoRefValidado = false
      this.form.codigo_referencia = null
      if (sanitized) {
        this.form.monto_descuento = ''
        this.formErrors.monto_descuento = ''
      }
    },

    async validarCodigoReferencia () {
      if (!this.form.codigoReferenciaInput) return
      this.codigoRefLoading = true
      this.codigoRefError = ''
      this.codigoRefSuccess = ''
      this.codigoRefValidado = false
      this.form.codigo_referencia = null
      try {
        const { data } = await this.$http.get('api/matriculas/codigos_referencia/verify_reference_code/', {
          params: { code: this.form.codigoReferenciaInput },
        })
        this.codigoRefSuccess = `Código válido — ${data.porcentaje_descuento}% de descuento`
        this.codigoRefValidado = true
        this.form.codigo_referencia = data.id
      } catch {
        this.codigoRefError = 'El código de referencia no es válido o no existe.'
        this.codigoRefValidado = false
      } finally {
        this.codigoRefLoading = false
      }
    },

    sendStartDate (item) {
      this.filters.page = 1
      this.filters.startDate = item
    },
    sendEndDate (item) {
      this.filters.page = 1
      this.filters.endDate = item
    },

    async fetchData () {
      clearTimeout(this.timeout)
      this.timeout = null
      this.timeout = setTimeout(async () => {
        await this.$router.replace({
          path: this.$route.path,
          query: {
            search:      this.searchGlobal,
            page:        this.filters.page,
            seccion:     this.filters.seccion || '',
            activo:      this.filters.activo ?? '',
            usado:       this.filters.usado != null ? this.filters.usado : '',
            solo_mios:   this.filters.soloMios ? 'true' : '',
            fecha_inicio: this.filters.startDate,
            fecha_fin:    this.filters.endDate,
          },
        }).catch(() => {})
        this.loading = true
        try {
          await this.fetchCodigos()
        } finally {
          this.loading = false
        }
      }, 500)
    },

    setInitialQueryParams () {
      const q = this.$route.query || {}
      this.filters = {
        startDate: q.fecha_inicio || '',
        endDate:   q.fecha_fin    || '',
        page:      Number(q.page  || 1),
        search:    q.search       || '',
        seccion:   q.seccion      || null,
        activo:    q.activo       ?? '',
        usado:     q.usado        || null,
        soloMios:  q.solo_mios !== 'false',
      }
    },

    onLocalSearchInput () {
      this.SET_SEARCH(this.searchLocal)
    },

    async openDialog (item) {
      this.dialog.editItem = item || null
      this.dialog.show = true
      this.formErrors = { seccion: '', precio_primera_cuota: '', monto_descuento: '', codigo_referencia: '' }
      this.pagoMinimo = null
      this.codigoRefError = ''
      this.codigoRefSuccess = ''
      this.codigoRefValidado = false
      if (item) {
        const seccionId = item.seccion?.id ?? item.seccion
        const refId = item.codigo_referencia?.id ?? item.codigo_referencia
        const refCodigo = item.codigo_referencia?.codigo || ''
        this.form = {
          seccion:              seccionId,
          precio_primera_cuota: item.precio_primera_cuota,
          monto_descuento:      item.monto_descuento != null ? item.monto_descuento : '',
          codigo_referencia:    refId,
          codigoReferenciaInput: refCodigo,
        }
        if (refId) {
          this.codigoRefSuccess = 'Código de referencia actual'
          this.codigoRefValidado = true
        }
        if (seccionId) {
          await this.onSeccionChange(seccionId)
        }
      } else {
        this.form = {
          seccion: null,
          precio_primera_cuota: '',
          monto_descuento: '',
          codigo_referencia: null,
          codigoReferenciaInput: '',
        }
      }
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation()
      })
    },

    closeDialog () {
      this.dialog.show = false
      this.dialog.editItem = null
      this.dialog.loading = false
      this.pagoMinimo = null
      this.codigoRefError = ''
      this.codigoRefSuccess = ''
      this.codigoRefValidado = false
    },

    async saveCode () {
      if (!this.$refs.form.validate()) return
      if (this.form.codigoReferenciaInput && !this.codigoRefValidado) {
        this.codigoRefError = 'Debes validar el código de referencia antes de guardar.'
        return
      }
      if (this.hasMontoDescuento && this.form.codigo_referencia) {
        this.formErrors.monto_descuento = 'No se puede combinar descuento fijo con código de referencia.'
        return
      }
      this.dialog.loading = true
      const montoRaw = String(this.form.monto_descuento || '').trim()
      const payload = {
        seccion_id:              this.form.seccion,
        precio_primera_cuota: this.form.precio_primera_cuota,
        codigo_referencia_id:    this.hasMontoDescuento ? null : (this.form.codigo_referencia || null),
        monto_descuento:         montoRaw !== '' ? montoRaw : null,
      }
      try {
        if (this.dialog.editItem) {
          await this.$http.patch(`${API_BASE}${this.dialog.editItem.id}/`, payload)
        } else {
          await this.$http.post(API_BASE, payload)
        }
        this.closeDialog()
        await this.fetchCodigos()
      } catch (e) {
        const err = e?.response?.data || {}
        this.formErrors.seccion              = err.seccion              ? err.seccion[0]              : ''
        this.formErrors.precio_primera_cuota = err.precio_primera_cuota ? err.precio_primera_cuota[0] : ''
        this.formErrors.monto_descuento      = err.monto_descuento      ? err.monto_descuento[0]      : ''
        this.formErrors.codigo_referencia    = err.codigo_referencia_id
          ? err.codigo_referencia_id[0]
          : (err.codigo_referencia ? err.codigo_referencia[0] : '')
        if (err.non_field_errors || err.detail) {
          this.formErrors.precio_primera_cuota =
            err.non_field_errors?.[0] || err.detail || this.formErrors.precio_primera_cuota
        }
      } finally {
        this.dialog.loading = false
      }
    },

    async confirmDeactivate (item) {
      const accion = item.activo ? 'desactivar' : 'activar'
      const accionTitle = item.activo ? 'Desactivar' : 'Activar'
      const ok = await this.$refs.confirmModal.open({
        title:      `${accionTitle} código`,
        message:    `¿Estás seguro de que deseas ${accion} el código <strong>${item.codigo}</strong>?`,
        acceptText: accionTitle,
        cancelText: 'Cancelar',
      })
      if (!ok) return
      this.$set(item, '_deactivating', true)
      try {
        const { data } = await this.$http.post(`${API_BASE}${item.id}/deactivate/`)
        this.$set(item, 'activo', data.activo)
      } catch (e) {
        console.error('Error al cambiar estado:', e?.response?.data)
      } finally {
        this.$set(item, '_deactivating', false)
      }
    },
  },

  async mounted () {
    if (this.$route.query.search) {
      await this.SET_SEARCH(this.$route.query.search)
    }
    this.searchLocal = this.searchGlobal || ''
    if (this.seccionesStore?.length) {
      this.secciones = this.seccionesStore
    } else {
      await this.fetchSecciones()
    }
    this.setInitialQueryParams()
    this.loading = true
    try {
      await this.fetchCodigos()
    } finally {
      this.loading = false
    }
  },
}
</script>

<style lang="scss" scoped>
.dialog {
  &__title {
    font-size: 20px;
    font-weight: 600;
    color: #455a64;
  }
}

.actions-btn {
  text-transform: none;
  letter-spacing: 0;
  box-shadow: none !important;
  border: 1px solid #ccc !important;
  background: #fff !important;
  color: #555;

  &--save {
    background: #009efb !important;
    border-color: #009efb !important;
    color: #fff !important;
  }

  &--danger {
    background: #f44336 !important;
    border-color: #f44336 !important;
    color: #fff !important;

    &:disabled {
      background: #ccc !important;
      border-color: #ccc !important;
      color: #fff !important;
    }
  }

  &--activate {
    background: #4caf50 !important;
    border-color: #4caf50 !important;
    color: #fff !important;
  }
}

.pagination {
  p {
    font-size: 14px;
  }
}
</style>
